import { useState, ChangeEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isDateInputValid,
  dateInputProcessor,
  applyInputDate,
  isDateInputLengthValid,
} from '../../utils/utils';

type UseDateInputProps = {
  defaultValue: string | undefined;
  onChangeDateInput?: (v: Date | null) => void;
  onDateClickOutSide?: () => void;
  setError?: (val: string | undefined) => void;
  requiredText?: string;
  disabled?: boolean;
  isDatePickerOpen?: boolean;
  dateBegin?: string;
};

export const useDateInput = ({
  onChangeDateInput,
  onDateClickOutSide,
  setError,
  defaultValue,
  requiredText,
  disabled,
  isDatePickerOpen,
  dateBegin,
}: UseDateInputProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const touched = useRef(false);

  useEffect(() => {
    if (defaultValue) {
      setError && setError(undefined);
      setValue(defaultValue);
      return;
    }

    if (!defaultValue) {
      setValue('');
    }
  }, [defaultValue]);

  useEffect(() => {
    disabled && setError && setError(undefined);
  }, [disabled]);

  const throwError = (text?: string) => {
    setError &&
      setError(text ?? t('discountEditor.step2.datePicker__invalidDateError'));

    onChangeDateInput && onChangeDateInput(null);

    setValue('');
  };

  const inputHandler = (e: ChangeEvent<HTMLInputElement>) => {
    // Close DatePicker calendar if it was opened
    onDateClickOutSide && onDateClickOutSide();

    // Clean error when we start entering something (input tooltip and border)
    setError && setError(undefined);

    const newValue = e.target.value;

    // Check onBackSpace action
    if (value.length > newValue.length) {
      setValue(newValue);
      return;
    }

    // Check if input values are only digits and slash after digit
    if (/\D^\//.test(newValue)) return;

    // Check are input values adequate
    if (!isDateInputValid(newValue)) return;

    // Auto paste slashes
    const processed = dateInputProcessor(newValue);

    // Show changes in input
    setValue((prev) => processed || prev);

    // If value is ready it can be applied in the DatePicker
    applyInputDate(processed, onChangeDateInput, throwError);
  };

  const blurHandler = () => {
    touched.current = true;

    if (!value && requiredText) {
      if (!isDatePickerOpen) {
        throwError(requiredText);
      }

      return;
    }

    if (dateBegin && value.split('/').reverse().join('-') < dateBegin) {
      throwError();
    }

    if (!isDateInputLengthValid(value)) {
      throwError();
    }
  };

  useEffect(() => {
    if (touched.current && !isDatePickerOpen) {
      blurHandler();
    }
  }, [isDatePickerOpen]);

  return { value, inputHandler, blurHandler };
};
